import { createAsyncThunk } from '@reduxjs/toolkit'
import { handleError, hasError } from '@repo/shared/api/helpers'
import {
  BeneficiaryComputedInsightWidgetDTO,
  BeneficiaryFiltersDTO,
  BeneficiaryInsightDimensionsDTO,
  BeneficiaryInsightDisplayType,
  BeneficiaryInsightMetricsDTO,
  BeneficiaryInsightWidgetDTO,
} from '@repo/shared/dtos/beneficiary'

import { AsyncThunkConfig, RejectValue } from '../actions'
import { getWidgetDefaultSize } from './helpers'

export const getComputedWidgets = createAsyncThunk<
  BeneficiaryComputedInsightWidgetDTO[],
  void,
  AsyncThunkConfig
>(
  'GET_BENEFICIARY_COMPUTED_INSIGHT_WIDGET_LIST',
  async (_, { getState, extra, rejectWithValue, fulfillWithValue }) => {
    const organizationId = getState().user.currentOrganizationId
    if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)
    const res = await extra.altApi.beneficiariesInsights.getComputedWidgets({ organizationId })
    if (hasError(res)) return rejectWithValue(handleError(res))
    return fulfillWithValue(res)
  },
)

export const getInsightWidget = createAsyncThunk<
  BeneficiaryInsightWidgetDTO,
  { widgetId: string },
  AsyncThunkConfig
>(
  'GET_BENEFICIARY_INSIGHT_WIDGET',
  async ({ widgetId }, { getState, extra, rejectWithValue, fulfillWithValue }) => {
    const organizationId = getState().user.currentOrganizationId
    if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)
    const res = await extra.altApi.beneficiariesInsights.getWidget({
      organizationId,
      widgetId: widgetId,
    })
    if (hasError(res)) return rejectWithValue(handleError(res))
    return fulfillWithValue(res)
  },
)

export const getComputedInsightWidget = createAsyncThunk<
  BeneficiaryComputedInsightWidgetDTO,
  { widget: BeneficiaryInsightWidgetDTO },
  AsyncThunkConfig
>(
  'GET_BENEFICIARY_COMPUTED_INSIGHT_WIDGET',
  async ({ widget }, { getState, extra, rejectWithValue, fulfillWithValue }) => {
    const organizationId = getState().user.currentOrganizationId
    if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)
    const res = await extra.altApi.beneficiariesInsights.getComputedWidget({
      organizationId,
      widgetId: widget.id,
    })
    if (hasError(res)) return rejectWithValue(handleError(res))
    return fulfillWithValue(res)
  },
)

export const downloadInsightWidgetCSV = createAsyncThunk<
  string,
  { widgetId: string },
  AsyncThunkConfig
>(
  'DOWNLOAD_BENEFICIARY_INSIGHT_WIDGET_CSV',
  async ({ widgetId }, { getState, extra, rejectWithValue, fulfillWithValue }) => {
    const organizationId = getState().user.currentOrganizationId
    if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)
    const res = await extra.altApi.beneficiariesInsights.getComputedWidget({
      organizationId,
      widgetId,
    })
    if (hasError(res)) return rejectWithValue(handleError(res))
    if (res.type !== 'chart' || !res.csv) {
      return rejectWithValue({ errors: [{ title: 'Widget is not a chart', statusCode: 400 }] })
    }
    return fulfillWithValue(res.csv)
  },
)

export const getAllWidgets = createAsyncThunk<
  BeneficiaryInsightWidgetDTO[],
  void,
  AsyncThunkConfig
>(
  'GET_BENEFICIARY_INSIGHT_WIDGET_LIST',
  async (_, { getState, extra, rejectWithValue, fulfillWithValue }) => {
    const organizationId = getState().user.currentOrganizationId
    if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)
    const res = await extra.altApi.beneficiariesInsights.getAllWidgets({ organizationId })
    if (hasError(res)) return rejectWithValue(handleError(res))
    return fulfillWithValue(res)
  },
)

export const addInsightWidget = createAsyncThunk<
  BeneficiaryInsightWidgetDTO,
  {
    title: string
    description: string
    type: BeneficiaryInsightDisplayType
    filters: BeneficiaryFiltersDTO
    dimensions: BeneficiaryInsightDimensionsDTO
    metrics: BeneficiaryInsightMetricsDTO
  },
  AsyncThunkConfig
>(
  'ADD_BENEFICIARY_INSIGHT_WIDGET',
  async (attributes, { getState, extra, rejectWithValue, fulfillWithValue }) => {
    const organizationId = getState().user.currentOrganizationId
    if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)

    const res = await extra.altApi.beneficiariesInsights.addWidget({
      organizationId,
      attributes: {
        ...attributes,
        ...getWidgetDefaultSize(attributes.type),
      },
    })

    if (hasError(res)) return rejectWithValue(handleError(res))
    return fulfillWithValue(res)
  },
)

export const updateInsightWidget = createAsyncThunk<
  BeneficiaryInsightWidgetDTO,
  { widgetId: string; widget: Partial<BeneficiaryInsightWidgetDTO> },
  AsyncThunkConfig
>(
  'UPDATE_BENEFICIARY_INSIGHT_WIDGET',
  async (attributes, { getState, extra, rejectWithValue, fulfillWithValue }) => {
    const organizationId = getState().user.currentOrganizationId
    if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)
    const res = await extra.altApi.beneficiariesInsights.updateWidget({
      organizationId,
      widgetId: attributes.widgetId,
      ...attributes.widget,
    })
    if (hasError(res)) return rejectWithValue(handleError(res))
    return fulfillWithValue(res)
  },
)

export const toggleWidgetDisplayInDashboard = createAsyncThunk<
  { widgetId: string; newValue: boolean },
  { widgetId: string },
  AsyncThunkConfig
>(
  'TOGGLE_BENEFICIARY_INSIGHT_WIDGET_DISPLAY_IN_DASHBOARD',
  async ({ widgetId }, { getState, extra, rejectWithValue, fulfillWithValue }) => {
    const organizationId = getState().user.currentOrganizationId
    if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)
    const res = await extra.altApi.beneficiariesInsights.toggleWidgetDisplayInDashboard({
      organizationId,
      widgetId,
    })
    if (hasError(res)) return rejectWithValue(handleError(res))
    return fulfillWithValue({ widgetId, newValue: res })
  },
)

export const deleteInsightWidget = createAsyncThunk<
  { widgetId: string },
  { widgetId: string },
  AsyncThunkConfig
>(
  'DELETE_BENEFICIARY_INSIGHT_WIDGET',
  async ({ widgetId }, { getState, extra, rejectWithValue, fulfillWithValue }) => {
    const organizationId = getState().user.currentOrganizationId
    if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)
    const res = await extra.altApi.beneficiariesInsights.deleteWidget({ organizationId, widgetId })
    if (hasError(res)) return rejectWithValue(handleError(res))
    return fulfillWithValue({ widgetId })
  },
)

export const updateInsightWidgetLayout = createAsyncThunk<boolean, void, AsyncThunkConfig>(
  'UPDATE_BENEFICIARY_INSIGHT_WIDGET_LAYOUT',
  async (_, { getState, extra, rejectWithValue, fulfillWithValue }) => {
    const organizationId = getState().user.currentOrganizationId
    if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)

    const layoutConfig = getState().beneficiaryInsights.layoutConfig

    if (!layoutConfig) return rejectWithValue(RejectValue.NoContent)

    await Promise.all(
      layoutConfig.map(async (config) => {
        const res = await extra.altApi.beneficiariesInsights.updateWidget({
          organizationId,
          widgetId: config.id,
          width: config.width,
          height: config.height,
          position: config.position,
        })
        if (hasError(res)) return rejectWithValue(handleError(res))
      }),
    )

    return fulfillWithValue(true)
  },
)
